import * as angular from "angular";
import * as _ from "lodash";
import { LocationModel } from "./location.model";
import { TeamColorDataProvider } from "../data-provider/team-color.dp";
import { User } from "./user.model";
import { Group } from "./group.model";

export interface EventLike {
    endtimestamp?: string | null;
}

/**
 * Whether an event's end time has passed. Works for any event-shaped object,
 * not only EventModel instances, so components can use it on bound data
 * without relying on the prototype being intact.
 */
export function eventHasEnded(event: EventLike): boolean {
    if (!event.endtimestamp) {
        return false;
    }
    return new Date(event.endtimestamp) < new Date();
}

export class EventModel {
    public readonly requestIgnoreList = [
        "LocationConstructor",
        "modelsUtils",
        "teamColorDataProvider",
    ];

    private today: Date;
    public id?: string;
    public location: LocationModel;
    public color?: string;
    public start: any;
    public end: any;
    public image: any;
    public effort: any;
    public sendInvites: any;
    public url: any;
    public licenses: number;
    public recurring: any;
    public attendance: any;
    public starttimestamp: string;
    public endtimestamp: string;
    public groups: Group[] = [];

    public eventid?: number | string;
    public address?: any;
    public addressinfo?: any;
    public latitude?: number | string;
    public longitude?: number | string;
    public virtual?: number | string;
    public name?: string;
    public creating?: any;
    public effortid?: string;
    public style?: string;
    public status?: string;
    public groupdate?: any;

    private modelsUtils: any;
    private teamColorDataProvider: TeamColorDataProvider;

    constructor(
        ModelsUtils: any,
        TeamColorDataProvider: TeamColorDataProvider,
        data?: object
    ) {
        this.modelsUtils = ModelsUtils;
        this.teamColorDataProvider = TeamColorDataProvider;

        this.today = new Date();
        if (data) {
            this.setData(data);
        } else {
            this.setData(this.getDefaultEventData(this.today));
        }
    }

    setData(data: any): void {
        if (data.eventid) {
            data.id = data.eventid;
        }

        if (data.starttimestamp) {
            // Safari fix
            data.start = {
                date: new Date(data.starttimestamp.replace(/-/g, "/")),
            };
            data.end = {
                date: new Date(data.endtimestamp.replace(/-/g, "/")),
            };
        }

        if (!data.location) {
            data.location = new LocationModel({
                address: data.address,
                description: data.addressinfo,
                latitude: data.latitude,
                longitude: data.longitude,
            });
        }

        if (data.recurring && data.recurring.isRecurring) {
            data.recurring.isRecurring = data.recurring.isRecurring == "1";
        }

        this.populateHours(data.start);
        this.populateHours(data.end);

        if (data.attendance) {
            angular.forEach(data.attendance, (list: any, key: any) => {
                let tempList = [];
                angular.forEach(list.users, (user: User) => {
                    tempList.push(this.modelsUtils.user(user));
                });
                data.attendance[key].users = tempList;
            });
        }

        _.merge(this, data);
        this.getColor();
    }

    getColor(): void {
        if (this.id) {
            this.color =
                this.teamColorDataProvider.teamColours[
                    (parseInt(this.id) * 333) %
                        this.teamColorDataProvider.teamColours.length
                ]; //gets a semi random color from colors array, that should always stay the same for same user
        }
    }

    isEnded(): boolean {
        return eventHasEnded(this);
    }

    private is24hr(): boolean {
        return document.cookie.split("; ").some((c) => c === "24hrTime=1");
    }

    populateHours(date?: any): void {
        if (date && !date.time) {
            let hours = date.date.getHours();
            let minutes =
                (date.date.getMinutes() < 10 ? "0" : "") +
                date.date.getMinutes();

            if (this.is24hr()) {
                let h = hours < 10 ? "0" + hours : hours.toString();
                date.time = {
                    hours: hours,
                    minutes: minutes,
                    period: "",
                    model: h + ":" + minutes,
                };
            } else {
                let period = "am";

                if (hours >= 12) {
                    hours -= 12;
                    period = "pm";
                }

                let modelHours = hours === 0 ? 12 : hours;
                let model = modelHours + ":" + minutes + period;

                date.time = {
                    hours: hours,
                    minutes: minutes,
                    period: period,
                    model: model,
                };
            }
        }
    }

    private getDefaultEventData(date: Date): object {
        let hours = date.getHours();
        let startHour = hours + 1;
        let endHour = hours + 2;
        let use24hr = this.is24hr();

        let startTime, endTime;

        if (use24hr) {
            let startH =
                startHour < 10 ? "0" + startHour : startHour.toString();
            let endH = endHour < 10 ? "0" + endHour : endHour.toString();
            startTime = {
                period: "",
                hours: startHour,
                minutes: "00",
                model: startH + ":00",
            };
            endTime = {
                period: "",
                hours: endHour,
                minutes: "00",
                model: endH + ":00",
            };
        } else {
            let startHourModel = startHour > 12 ? startHour - 12 : startHour;
            let startPeriod = startHour >= 12 ? "pm" : "am";
            let endHourModel = endHour > 12 ? endHour - 12 : endHour;
            let endPeriod = endHour >= 12 ? "pm" : "am";
            startTime = {
                period: startPeriod,
                hours: startHour,
                minutes: "00",
                model: startHourModel + ":00" + startPeriod,
            };
            endTime = {
                period: endPeriod,
                hours: endHour,
                minutes: "00",
                model: endHourModel + ":00" + endPeriod,
            };
        }

        return {
            location: new LocationModel(),
            start: {
                date: _.cloneDeep(date),
                time: startTime,
            },
            end: {
                date: _.cloneDeep(date),
                time: endTime,
            },
            image: null,
            effort: null,
            sendInvites: false,
            url: null,
            licenses: 0,
            recurring: {
                isRecurring: false,
                frequency: "day",
                month: date.getMonth(),
                date: date.getDate(),
                days: [date.getDay()],
                endDate: _.cloneDeep(date),
                occurrences: 5,
                ends: "never",
                start: use24hr
                    ? { period: "", hours: 13, minutes: "00", model: "13:00" }
                    : {
                          period: "pm",
                          hours: 1,
                          minutes: "00",
                          model: "1:00pm",
                      },
                end: use24hr
                    ? { period: "", hours: 14, minutes: "00", model: "14:00" }
                    : {
                          period: "pm",
                          hours: 2,
                          minutes: "00",
                          model: "2:00pm",
                      },
            },
            attendance: {
                invited: {
                    type: "everyone",
                    users: [],
                    teams: [],
                    permissions: [],
                    count: 0,
                },
                attending: {
                    count: 0,
                    users: [],
                },
                all: {
                    count: 0,
                    users: [],
                },
                waiting: {
                    count: 0,
                    users: [],
                },
                declined: {
                    count: 0,
                    users: [],
                },
                uninvited: {
                    count: 0,
                    users: [],
                },
            },
        };
    }
}
